/**
 * useRoomSession (PPL-33) — shared joint-room hook.
 *
 * Mirrors `useRoomPrep` in structure but targets the shared room:
 *  - Realtime `onSnapshot` on `mediations/{id}/roomMessages` via
 *    `subscribeToRoomMessages` — NOT polling, NOT CircleChat's 5s interval.
 *  - Root doc subscription so the page can read `lastMediatorTurnAt`, `state`,
 *    `inviterUid`/`inviteeUid`, and surface state changes in real time.
 *  - `sendMessage` — calls `sendRoomMessage` (`sendMediationMessageEurW`).
 *  - `requestMediatorTurn` — calls the 20 s cooldown-protected callable.
 *  - `markRoomSeen` — called on focus / scroll-to-bottom.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  subscribeToRoom,
  subscribeToRoomMessages,
  sendRoomMessage,
  requestMediatorTurn,
  markRoomSeen,
} from '../services/roomService';
import type { SubscriptionMeta } from '../services/roomService';
import type { Room, RoomMessage } from '../types/room';
import type { TimestampLike } from '../types/pplTalk';
import type { FirebaseTimestamp } from '../types/pplTalk';

/** 20 s cooldown in ms — mirrors the server-side guard on requestMediatorTurnEurW. */
const MEDIATOR_COOLDOWN_MS = 20_000;

function toMs(value: TimestampLike): number | null {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return new Date(value).getTime();
  // FirebaseTimestamp
  const ts = value as FirebaseTimestamp;
  if (typeof ts.toDate === 'function') return ts.toDate!().getTime();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return null;
}

export interface UseRoomSessionResult {
  mediation: Room | null;
  messages: RoomMessage[];
  side: 'inviter' | 'invitee' | null;
  isSending: boolean;
  isRequestingTurn: boolean;
  initialLoadComplete: boolean;
  error: string | null;
  /** Remaining cooldown seconds before another mediator turn can be requested. */
  mediatorCooldownRemaining: number;
  /**
   * True when the room message subscription has hit the 100-message cap.
   * Consumers should render an archived-messages notice.
   */
  atLimit: boolean;
  sendMessage: (text: string) => Promise<void>;
  requestTurn: () => Promise<{ throttled?: boolean }>;
  markSeen: () => void;
}

export default function useRoomSession(
  mediationId: string | null | undefined,
  uid: string | null | undefined,
): UseRoomSessionResult {
  const [mediation, setRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<RoomMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isRequestingTurn, setIsRequestingTurn] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [atLimit, setAtLimit] = useState(false);

  // Tick every second so the cooldown countdown updates.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  // Root doc subscriber (state, lastMediatorTurnAt, inviter/inviteeUid …).
  useEffect(() => {
    if (!mediationId) return undefined;
    const unsub = subscribeToRoom(
      mediationId,
      (data) => setRoom(data),
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('subscribeToRoom error:', err);
        setError(err?.message ?? 'failed to load mediation');
      },
    );
    return unsub;
  }, [mediationId]);

  // Room messages subscriber.
  useEffect(() => {
    if (!mediationId) return undefined;
    const unsub = subscribeToRoomMessages(
      mediationId,
      (msgs) => {
        setMessages(msgs);
        setInitialLoadComplete(true);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('subscribeToRoomMessages error:', err);
        setError(err?.message ?? 'failed to load messages');
        setInitialLoadComplete(true);
      },
      (meta: SubscriptionMeta) => setAtLimit(meta.atLimit),
    );
    return unsub;
  }, [mediationId]);

  // Derive caller side.
  const side = useMemo<'inviter' | 'invitee' | null>(() => {
    if (!mediation || !uid) return null;
    if (mediation.inviterUid === uid) return 'inviter';
    if (mediation.inviteeUid === uid) return 'invitee';
    return null;
  }, [mediation, uid]);

  // Cooldown remaining in seconds.
  const mediatorCooldownRemaining = useMemo(() => {
    const lastMs = toMs(mediation?.lastMediatorTurnAt ?? null);
    if (lastMs == null) return 0;
    const elapsed = now - lastMs;
    return Math.max(0, Math.ceil((MEDIATOR_COOLDOWN_MS - elapsed) / 1_000));
  }, [mediation, now]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !mediationId || isSending) return;
      setIsSending(true);
      try {
        const result = await sendRoomMessage(mediationId, trimmed);
        // Moderation blocks resolve as { success: false, reason } rather than
        // throwing — turn that into an error so the message doesn't vanish
        // without a word. The composer restores the text so they can rephrase.
        if (result?.success === false) {
          throw new Error(
            'that message was held back — let’s keep it respectful in the room. try rephrasing.',
          );
        }
      } catch (err) {
        // Don't write the persistent load-`error` here — a send/moderation
        // failure is transient and surfaces via the caller's auto-dismissing
        // `sendError`. Writing it here would keep the banner up forever.
        throw err;
      } finally {
        setIsSending(false);
      }
    },
    [mediationId, isSending],
  );

  const requestTurn = useCallback(async () => {
    if (!mediationId || isRequestingTurn || mediatorCooldownRemaining > 0) {
      return {};
    }
    setIsRequestingTurn(true);
    try {
      return await requestMediatorTurn(mediationId);
    } catch (err) {
      // Transient — do NOT set the persistent load `error` (it drives an always-on
      // banner). Callers surface it: manual ask-f8 shows a brief auto-dismissing
      // notice; the auto-intro stays silent (PPL-155 review).
      throw err;
    } finally {
      setIsRequestingTurn(false);
    }
  }, [mediationId, isRequestingTurn, mediatorCooldownRemaining]);

  const markSeen = useCallback(() => {
    if (!mediationId) return;
    markRoomSeen(mediationId).catch(() => {});
  }, [mediationId]);

  return {
    mediation,
    messages,
    side,
    isSending,
    isRequestingTurn,
    initialLoadComplete,
    error,
    mediatorCooldownRemaining,
    atLimit,
    sendMessage,
    requestTurn,
    markSeen,
  };
}
